// featured-share.js - the "Run what others are running" landing card.
//
// On landing load it fetches GET /api/shares/featured (admin-curated, PII-free, public) and
// renders one card per featured shared run: the label, the REAL server-tracked run count, a GTI
// verify badge, and an Open link to /s/{token}.
//
// The card VISUAL is a LIVE graph, not a screenshot: for a plugin:std: share the card loads the
// (free, deterministic, pure-JS) plugin, runs it on-device with the share's own frozen params,
// and draws its first chart with Chart.js. This matches the product's "proof you can check, not a
// screenshot" line - the plot is recomputed in the visitor's browser. If the plugin produces no
// chart (or anything fails), the card falls back to the share's captured OG image, then to a
// branded placeholder - so a card is never blank and never shows a fabricated plot.
//
// Honesty: zero featured runs -> the whole section stays hidden (no empty state, no invented
// "trending" number). Counts are exactly what the server tracked.
// Theme: the landing is LIGHT (white cards) per repo convention.

import { api } from './api.js';

function esc(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// "N run" / "N runs" - honest singular/plural, no rounding or inflation.
function runsLabel(n) {
  const v = Number(n) || 0;
  return v === 1 ? '1 run' : v.toLocaleString() + ' runs';
}

function cardHtml(item, idx) {
  const href = '/s/' + encodeURIComponent(item.token);
  const label = esc(item.label) || 'Shared result';
  // The media strip starts as the captured image if one exists, else a branded placeholder. The
  // live-chart pass (below) upgrades it to a real computed plot when the plugin yields one.
  const media = item.image
    ? `<div class="fs-card-media" data-slot="${idx}"><img src="${esc(item.image)}" alt="${label}" loading="lazy"></div>`
    : `<div class="fs-card-media fs-card-media-ph" data-slot="${idx}"><i class="fas fa-chart-line"></i><span>Open to run it live</span></div>`;
  return `
    <a class="fs-card" href="${esc(href)}">
      ${media}
      <div class="fs-card-body">
        <div class="fs-card-label">${label}</div>
        <div class="fs-card-meta">
          <span class="fs-card-metaleft">
            <span class="fs-card-runs"><i class="fas fa-play"></i> ${esc(runsLabel(item.runs))}</span>
            <span class="fs-card-gti" title="GeoNum Trust Index - a verifiable result. Open it to re-run on your own device and check the verdict."><span class="fs-gti-t">GTI</span><span class="fs-gti-v"><i class="fas fa-shield-halved"></i> verify</span></span>
          </span>
          <span class="fs-card-open">Open <i class="fas fa-arrow-right"></i></span>
        </div>
      </div>
    </a>`;
}

function injectStyles() {
  if (document.getElementById('fs-styles')) return;
  const css = `
  #landing-featured .fs-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:16px; margin-top:8px; }
  #landing-featured .fs-card { display:flex; flex-direction:column; text-decoration:none; color:inherit;
    background:#ffffff; border:1px solid #e2e8f0; border-radius:12px; overflow:hidden;
    box-shadow:0 1px 3px rgba(15,23,42,0.06); transition:box-shadow .15s ease, transform .15s ease; }
  #landing-featured .fs-card:hover { box-shadow:0 8px 24px rgba(15,23,42,0.12); transform:translateY(-2px); }
  #landing-featured .fs-card-media { background:#0f172a; position:relative; height:132px; }
  #landing-featured .fs-card-media img { display:block; width:100%; height:132px; object-fit:cover; }
  #landing-featured .fs-card-media canvas { display:block; width:100% !important; height:132px !important; padding:8px 10px; box-sizing:border-box; }
  #landing-featured .fs-card-media-ph { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px;
    background:linear-gradient(135deg,#0f172a 0%,#1e293b 55%,#312e81 100%); color:#c7d2fe; }
  #landing-featured .fs-card-media-ph i { font-size:26px; opacity:.85; }
  #landing-featured .fs-card-media-ph span { font-size:11px; letter-spacing:.02em; color:#a5b4fc; }
  #landing-featured .fs-card-body { padding:14px 16px; display:flex; flex-direction:column; gap:10px; }
  #landing-featured .fs-card-label { font-weight:700; font-size:14px; color:#1e293b; line-height:1.35; }
  #landing-featured .fs-card-meta { display:flex; align-items:center; justify-content:space-between; font-size:12px; gap:8px; }
  #landing-featured .fs-card-metaleft { display:inline-flex; align-items:center; gap:8px; }
  #landing-featured .fs-card-runs { color:#047857; font-weight:600; }
  #landing-featured .fs-card-runs i { font-size:9px; margin-right:3px; }
  #landing-featured .fs-card-gti { display:inline-flex; align-items:center; border-radius:4px; overflow:hidden; font-size:10px; font-weight:700; line-height:1; }
  #landing-featured .fs-gti-t { background:#1e293b; color:#fff; padding:3px 5px; letter-spacing:.02em; }
  #landing-featured .fs-gti-v { background:#ecfdf5; color:#047857; padding:3px 6px; }
  #landing-featured .fs-gti-v i { font-size:9px; margin-right:2px; }
  #landing-featured .fs-card-open { color:#7c3aed; font-weight:600; white-space:nowrap; }
  #landing-featured .fs-card-open i { font-size:10px; margin-left:3px; }`;
  const el = document.createElement('style'); el.id = 'fs-styles'; el.textContent = css; document.head.appendChild(el);
}

// A parsed plugin:std:{id} target -> the plugin id, else null (only std plugins are safe to run
// here - free, deterministic, no entitlement, no server call).
function stdPluginId(target) {
  const m = /^plugin:std:([\w.-]+)$/.exec(String(target || ''));
  return m ? m[1] : null;
}

// Minimal, side-effect-free plugin host so a plugin's run() can execute headlessly here. It
// returns its chart data in the result object (res.charts); we render that ourselves, so the
// host's chart/viewport/engine hooks are no-ops. A plugin that actually needs a wasm engine will
// throw on host.engine() and we fall back - std plugins are pure JS and do not.
const HEADLESS_HOST = {
  log() {}, trustHTML() { return ''; }, showTrust() {}, chart() {}, viewport3d() {},
  engine() { throw new Error('no engine on landing'); }, _lastResult: null,
};

const _pluginCache = new Map();
async function loadStdPlugin(id) {
  if (_pluginCache.has(id)) return _pluginCache.get(id);
  const p = (async () => {
    // Direct /plugins/std/*.plugin.es is 403'd by the static-code gate (STIG V-AUTHZ-004). Plugin
    // code is served through the gated endpoint; std plugins flagged demoExposed are anonymous there,
    // which is exactly the set the landing page features. A non-demo id just 403s and the caller
    // (renderLiveChart) leaves the captured image / placeholder untouched.
    const res = await fetch('/api/plugin-code/std/' + encodeURIComponent(id), { cache: 'force-cache' });
    if (!res.ok) throw new Error('plugin ' + id + ': HTTP ' + res.status);
    const src = await res.text();
    const url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
    try { return (await import(/* @vite-ignore */ url)).default; } finally { URL.revokeObjectURL(url); }
  })();
  _pluginCache.set(id, p);
  return p;
}

// Build a clean thumbnail Chart.js config from a plugin chart spec
// { type, x, series:[{name,y,color}], ... }. Axis/legend hidden for a sparkline look.
function chartConfig(spec) {
  const type = (spec.type === 'scatter' || spec.type === 'bar') ? spec.type : 'line';
  const datasets = (spec.series || []).filter(s => Array.isArray(s.y)).map(s => ({
    label: s.name || '', data: s.y, borderColor: s.color || '#7c3aed',
    backgroundColor: (s.color || '#7c3aed') + '22', borderWidth: 1.6, pointRadius: 0,
    fill: false, tension: 0.15,
  }));
  if (!datasets.length) return null;
  return {
    type,
    data: { labels: (spec.x || []).map(v => (typeof v === 'number' ? v : String(v))), datasets },
    options: {
      responsive: true, maintainAspectRatio: false, animation: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: { x: { display: false }, y: { display: false } },
      elements: { point: { radius: 0 } },
    },
  };
}

// Try to draw a card's real chart, live, from its own frozen params. Any failure leaves the
// existing media (captured image or placeholder) untouched - never a blank, never a fake plot.
async function renderLiveChart(item) {
  try {
    if (item.image) return;                       // already has a real captured chart
    if (!window.Chart) return;                    // Chart.js not present - keep the placeholder
    const id = stdPluginId(item.target);
    if (!id) return;
    const slot = document.querySelector('#featured-share-grid .fs-card-media[data-slot="' + CSS.escape(String(item._slot)) + '"]');
    if (!slot) return;

    const plugin = await loadStdPlugin(id);
    if (!plugin || typeof plugin.run !== 'function') return;
    let params = item.params;
    if (typeof params === 'string') { try { params = JSON.parse(params); } catch (_) { params = {}; } }
    const res = await plugin.run(params || {}, HEADLESS_HOST);
    const spec = res && Array.isArray(res.charts) ? res.charts.find(c => c && Array.isArray(c.series)) : null;
    const cfg = spec ? chartConfig(spec) : null;
    if (!cfg) return;

    // Swap the placeholder for a canvas and draw. Keep the dark media background for contrast.
    slot.classList.remove('fs-card-media-ph');
    slot.innerHTML = '';
    const canvas = document.createElement('canvas');
    slot.appendChild(canvas);
    // eslint-disable-next-line no-new
    new window.Chart(canvas.getContext('2d'), cfg);
  } catch (_) { /* keep the fallback media on any error */ }
}

export async function initFeaturedShare() {
  const section = document.getElementById('landing-featured');
  const grid = document.getElementById('featured-share-grid');
  if (!section || !grid) return;
  section.style.display = 'none';   // reveal only once we confirm real content

  let res;
  try { res = await api.listFeaturedShares(); }
  catch { return; }

  const items = (res && res.ok && res.data && res.data.featured) || [];
  if (!items.length) return;   // zero featured = hide entirely

  items.forEach((it, i) => { it._slot = i; });
  injectStyles();
  grid.innerHTML = items.map((it, i) => cardHtml(it, i)).join('');
  section.style.display = '';

  // Upgrade each imageless card to a live computed chart, non-blocking (the strip is already up).
  items.forEach(it => { renderLiveChart(it); });
}

export default { initFeaturedShare };
